import {EmptyPolicyNumber, PolicyNumber, ValidPolicyNumber} from 'ApplicationEntry/models/EnteredPolicyNumber';
import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {isLoaded as isLoadedPackages, PackageModel, UnassignedPackageModel} from '../PackageModel';

type PinCareProps = {
  packageName: StringUnion;
  planType: StringUnion;
  premium: StringUnion;
  calculatedPremium: StringUnion;
  cwa: StringUnion;
  replacement: boolean;
  policyNumber: PolicyNumber;
  packageList: PackageModel;
  units: StringUnion;
  jStamp: boolean;
  situsState: StringUnion;
};

export class InvalidOrdinaryPinCare {
  public readonly key = 'InvalidOrdinaryPinCare';

  constructor(
    public readonly packageName: StringUnion,
    public readonly planType: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly replacement: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly packageList: PackageModel,
    public readonly units: StringUnion,
    public readonly jStamp: boolean,
    public readonly situsState: StringUnion
  ) {}
}

export class ValidOrdinaryPinCare {
  public static createApp({
    packageName,
    planType,
    premium,
    calculatedPremium,
    cwa,
    replacement,
    policyNumber,
    packageList,
    units,
    jStamp,
    situsState,
  }: PinCareProps) {
    return this.create(
      packageName,
      planType,
      premium,
      calculatedPremium,
      cwa,
      replacement,
      policyNumber,
      packageList,
      units,
      jStamp,
      situsState
    );
  }

  public static create(
    packageName: StringUnion,
    planType: StringUnion,
    premium: StringUnion,
    calculatedPremium: StringUnion,
    cwa: StringUnion,
    replacement: boolean,
    policyNumber: PolicyNumber,
    packageList: PackageModel,
    units: StringUnion,
    jStamp: boolean,
    situsState: StringUnion
  ) {
    return isNonEmptyString(packageName) &&
      planType &&
      isNonEmptyString(premium) &&
      isNonEmptyString(cwa) &&
      replacement &&
      policyNumber &&
      isLoadedPackages(packageList)
      ? new ValidOrdinaryPinCare(
          packageName,
          planType,
          premium,
          calculatedPremium,
          cwa,
          replacement,
          policyNumber,
          packageList,
          units,
          jStamp,
          situsState
        )
      : new InvalidOrdinaryPinCare(
          packageName,
          planType,
          premium,
          calculatedPremium,
          cwa,
          replacement,
          policyNumber,
          packageList,
          units,
          jStamp,
          situsState
        );
  }

  public readonly key = 'ValidOrdinaryPinCare';
  constructor(
    public readonly packageName: StringUnion,
    public readonly planType: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly replacement: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly packageList: PackageModel,
    public readonly units: StringUnion,
    public readonly jStamp: boolean,
    public readonly situsState: StringUnion
  ) {}
}

export type OrdinaryPinCare = InvalidOrdinaryPinCare | ValidOrdinaryPinCare;

export type OrdinaryPinCareOptions<TReturn> = ToPartialMatchOptions<OrdinaryPinCare, 'key', TReturn>;

export const matchOrdinaryPinCare = <TReturn>(options: OrdinaryPinCareOptions<TReturn>) => (
  app: OrdinaryPinCare
): TReturn => {
  switch (app.key) {
    case 'InvalidOrdinaryPinCare':
      return options.InvalidOrdinaryPinCare ? options.InvalidOrdinaryPinCare(app) : options.default(app);
    case 'ValidOrdinaryPinCare':
      return options.ValidOrdinaryPinCare ? options.ValidOrdinaryPinCare(app) : options.default(app);
  }
};

export const EmptyOrdinaryPinCare = new InvalidOrdinaryPinCare(
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  EmptyPolicyNumber,
  new UnassignedPackageModel(),
  new EmptyString(),
  false,
  new EmptyString()
);

export const changePackage = (packageName: string) => (app: OrdinaryPinCare) =>
  ValidOrdinaryPinCare.createApp({...app, packageName: NonEmptyString.create(packageName)});

export const changePlanType = (planType: string) => (app: OrdinaryPinCare) =>
  ValidOrdinaryPinCare.createApp({...app, planType: NonEmptyString.create(planType)});

export const changePremium = (premium: string) => (app: OrdinaryPinCare) =>
  ValidOrdinaryPinCare.createApp({...app, premium: NonEmptyString.create(premium)});

export const changeCalculatedPremium = (calculatedPremium: string) => (app: OrdinaryPinCare) =>
  ValidOrdinaryPinCare.createApp({...app, calculatedPremium: NonEmptyString.create(calculatedPremium)});

export const changeCWA = (cwa: string) => (app: OrdinaryPinCare) =>
  ValidOrdinaryPinCare.createApp({...app, cwa: NonEmptyString.create(cwa)});

export const changeReplacement = (replacement: boolean) => (app: OrdinaryPinCare) =>
  ValidOrdinaryPinCare.createApp({...app, replacement, policyNumber: ValidPolicyNumber.create(new EmptyString())});

export const changePolicyNumber = (policyNumber: StringUnion) => (app: OrdinaryPinCare) =>
  ValidOrdinaryPinCare.createApp({...app, policyNumber: ValidPolicyNumber.create(policyNumber)});

export const changePackageList = (packages: PackageModel) => (app: OrdinaryPinCare) =>
  ValidOrdinaryPinCare.createApp({...app, packageList: packages});

export const changeUnits = (units: string) => (app: OrdinaryPinCare) =>
  ValidOrdinaryPinCare.createApp({...app, units: NonEmptyString.create(units)});

export const changeJStamp = (jStamp: boolean) => (app: OrdinaryPinCare) =>
  ValidOrdinaryPinCare.createApp({...app, jStamp});

export const changeSitusState = (situsState: string) => (app: OrdinaryPinCare) =>
  ValidOrdinaryPinCare.createApp({...app, situsState: NonEmptyString.create(situsState)});
